import { useState, useMemo } from 'react';
import {
  Settings2,
  Search,
  CheckCircle2,
  XCircle,
  Save,
  Undo2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { FeatureCategorySection } from '@/components/features/feature-category-section';
import type { FeatureMode } from '@/types/features';
import { toast } from 'sonner';
import { useFeatureSettingsPermissions } from '@/hooks/use-permissions';

const categoryConfig = [
  {
    id: 'core-operations',
    name: 'Core Operations',
    description: 'Essential features for daily asset management operations',
  },
  {
    id: 'transaction-management',
    name: 'Transaction Management',
    description: 'Financial transactions and asset value tracking',
  },
  {
    id: 'operational-tools',
    name: 'Operational Tools',
    description: 'Tools for asset tracking and compliance management',
  },
  {
    id: 'analytics-reporting',
    name: 'Analytics & Reporting',
    description: 'Data insights and historical tracking capabilities',
  },
  {
    id: 'system-administration',
    name: 'System Administration',
    description: 'Bulk operations and data management tools',
  },
  {
    id: 'configuration',
    name: 'Configuration',
    description: 'System settings and reference data management',
  },
];

export default function FeaturesSettingsPage() {
  const {
    modules,
    isLoading,
    isSaving,
    isDirty,
    recentlyChanged,
    switchMode,
    switchAllToMode,
    getModulesByCategory,
    saveSettings,
    discardChanges,
  } = useFeatureSettings();

  const { canManage: canManageFeatures } = useFeatureSettingsPermissions();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter modules
  const filteredModules = useMemo(() => {
    let filtered = Object.values(modules);

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        m =>
          m.name.toLowerCase().includes(query) ||
          m.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [modules, searchQuery]);

  const handleModeChange = async (moduleId: string, newMode: FeatureMode) => {
    const module = modules[moduleId];
    if (!module) return;

    const success = await switchMode(moduleId, newMode);

    if (!success) {
      toast.error('Failed to Change Mode', {
        description: 'An error occurred while switching modes. Please try again.',
      });
    }
  };

  const handleBulkAction = async (mode: FeatureMode) => {
    const success = await switchAllToMode(mode);

    if (!success) {
      toast.error('Bulk Action Failed', {
        description: 'An error occurred during bulk operation. Please try again.',
      });
    }
  };

  const handleSaveSettings = async () => {
    const success = await saveSettings();
    if (success) {
      toast.success('Settings Saved', {
        description: 'Your feature settings have been saved successfully.',
      });
    } else {
      toast.error('Failed to Save Settings', {
        description: 'An error occurred while saving settings. Please try again.',
      });
    }
  };

  const handleDiscardChanges = () => {
    discardChanges();
    toast.info('Changes Discarded', {
      description: 'Your unsaved changes have been discarded.',
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Settings2 className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">Feature Settings</h1>
            </div>
                                <p className="text-sm text-muted-foreground mt-1">
                        Manage module modes and configure feature availability
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="relative w-[200px]">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Search features..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
            
                      {canManageFeatures && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBulkAction('live')}
                          disabled={isLoading}
                          className="gap-2"
                        >
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          All to Live
                        </Button>
                      )}
                      {canManageFeatures && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBulkAction('demo')}
                          disabled={isLoading}
                          className="gap-2"
                        >
                          <XCircle className="h-4 w-4 text-gray-600" />
                          All to Demo
                        </Button>
                      )}
                    </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-8">
          {filteredModules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No features found</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Try adjusting your search query or filters to find what you're looking for.
              </p>
            </div>
          ) : (
            categoryConfig.map((category) => {
              const categoryModules = getModulesByCategory(category.id).filter(module =>
                filteredModules.some(fm => fm.id === module.id)
              );

              return (
                <FeatureCategorySection
                  key={category.id}
                  title={category.name}
                  description={category.description}
                  modules={categoryModules}
                  onModeChange={handleModeChange}
                  isLoading={isLoading}
                  recentlyChanged={recentlyChanged}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Footer with Save/Discard buttons */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {isDirty ? (
              <span className="text-amber-600 font-medium">You have unsaved changes</span>
            ) : (
              <span>All changes saved</span>
            )}
          </div>
          {canManageFeatures && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscardChanges}
                disabled={!isDirty || isSaving}
                className="gap-2"
              >
                <Undo2 className="h-4 w-4" />
                Discard Changes
              </Button>
              <Button
                size="sm"
                onClick={handleSaveSettings}
                disabled={!isDirty || isSaving}
                className="gap-2"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
